<template>
  <div class="page-wrap">
    <h1 class="page-title">Accounting Tree</h1>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <div class="page-center">
        <div class="page-container">

          <!-- Loading -->
          <div v-if="loading" class="state-msg">Loading Data ...</div>
          <div v-else-if="error" class="state-msg state-msg--err">{{ error }}</div>

          <!-- Popup Add/Edit/Delete -->
          <div v-if="menu.open" class="menu-overlay" @click="closeMenu">
            <div
              class="menu-box"
              :style="{ top: menu.top + 'px', left: menu.left + 'px' }"
              @click.stop
            >
              <button v-if="can.create" class="menu-item" @click="onMenuAction('add')">
                <PlusIcon class="menu-ic" />
                <span>Add</span>
              </button>
              <button v-if="can.edit" class="menu-item" @click="onMenuAction('edit')">
                <EditIcon class="menu-ic" />
                <span>Edit</span>
              </button>
              <button
                v-if="can.edit && menu.targetType === 'node'"
                class="menu-item menu-item--danger"
                @click="onMenuAction('delete')"
              >
                <TrashIcon class="menu-ic" />
                <span>Delete</span>
              </button>
            </div>
          </div>

          <!-- Modal Add/Edit Account -->
          <div v-if="accountModal.open" class="acc-overlay" @click="closeAccountModal">
            <div class="acc-modal" @click.stop>
              <button class="acc-x" @click="closeAccountModal" aria-label="Close">×</button>
              <h2 class="acc-title">
                {{ accountModal.mode === 'add' ? 'Add Account' : 'Edit Account' }}
              </h2>
              <label class="acc-label">Account Name</label>
              <input
                class="acc-input"
                type="text"
                placeholder="Enter account name"
                v-model="accountModal.name"
                @keyup.enter="submitAccountModal"
              />
              <button class="acc-btn" :disabled="accountModal.saving" @click="submitAccountModal">
                {{ accountModal.saving ? '...' : (accountModal.mode === 'add' ? 'Add' : 'Save') }}
              </button>
            </div>
          </div>

          <template v-if="!loading && !error">
            <div class="grid-2">
              <TreeBlock
                v-for="(root, idx) in treeRoots.slice(0, 2)"
                :key="root.id"
                :root="root"
                :rootIndex="idx"
              />
            </div>
            <div class="grid-2 mt-40">
              <TreeBlock
                v-for="(root, idx) in treeRoots.slice(2)"
                :key="root.id"
                :root="root"
                :rootIndex="idx + 2"
              />
            </div>
          </template>

        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, reactive, ref, onMounted } from 'vue';
import api from '@/api';
import { loadMyPermissions, hasAny } from '@/utils/permissions';

/* ===== Permissions ===== */
const can = reactive({
  view:   false, // accounting_tree.view
  create: false, // accounting_tree.create
  edit:   false, // accounting_tree.edit
});

const loading   = ref(true);
const error     = ref(null);
const treeRoots = reactive([]);

async function fetchTree() {
  loading.value = true;
  error.value   = null;
  try {
    const res = await api.get('/accounting/accounting-tree');
    treeRoots.splice(0, treeRoots.length, ...res.data);
    initExpanded();
  } catch (e) {
    error.value = 'Failed to load accounting tree.';
    console.error(e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await loadMyPermissions(false);
  can.view   = hasAny(['accounting_tree.view']);
  can.create = hasAny(['accounting_tree.create']);
  can.edit   = hasAny(['accounting_tree.edit']);

  if (!can.view) {
    loading.value = false;
    return;
  }

  await fetchTree();
});

const expanded     = reactive({});
const rootExpanded = reactive({});

function initExpanded() {
  treeRoots.forEach(r => {
    rootExpanded[r.id] = false;
  });
}

function toggleRoot(id)   { rootExpanded[id] = !rootExpanded[id]; }
function toggleFolder(id) { expanded[id] = !expanded[id]; }

const menu = reactive({
  open: false, top: 0, left: 0,
  targetType: 'node',
  node: null, root: null,
  parentList: null,
  rootObj:    null,
});

function positionMenuByButton(btnEl) {
  const rect       = btnEl.getBoundingClientRect();
  const width      = 220, padding = 12;
  const menuHeight = 130;
  let left = rect.right - width;
  left = Math.max(padding, Math.min(left, window.innerWidth - width - padding));
  const spaceBelow = window.innerHeight - rect.bottom;
  const top = spaceBelow < menuHeight + 20
    ? rect.top - menuHeight - 10
    : rect.bottom + 10;

  menu.top  = top;
  menu.left = left;
}

function openMenu(e, node, parentList, rootObj) {
  if (!can.create && !can.edit) return;
  positionMenuByButton(e.currentTarget);
  menu.open = true; menu.targetType = 'node';
  menu.node = node; menu.root = null;
  menu.parentList = parentList;
  menu.rootObj    = rootObj;
}

function openRootMenu(e, root) {
  if (!can.create && !can.edit) return;
  positionMenuByButton(e.currentTarget);
  menu.open = true; menu.targetType = 'root';
  menu.root = root; menu.node = null;
  menu.parentList = null; menu.rootObj = null;
}

function closeMenu() {
  menu.open = false; menu.node = null; menu.root = null;
  menu.parentList = null; menu.rootObj = null;
}

/* Modal */
const accountModal = reactive({
  open: false, mode: 'add', saving: false,
  targetType: 'node',
  node: null, root: null,
  parentList: null, rootObj: null,
  name: '',
});

function openAccountModal(mode, targetType, node, root, parentList, rootObj) {
  accountModal.mode       = mode;
  accountModal.open       = true;
  accountModal.saving     = false;
  accountModal.targetType = targetType;
  accountModal.node       = node;
  accountModal.root       = root;
  accountModal.parentList = parentList;
  accountModal.rootObj    = rootObj;
  accountModal.name       = mode === 'edit'
    ? (targetType === 'root' ? (root?.name ?? '') : (node?.name ?? '')).trim()
    : '';
}

function closeAccountModal() {
  accountModal.open       = false;
  accountModal.saving     = false;
  accountModal.node       = null; accountModal.root = null;
  accountModal.parentList = null; accountModal.rootObj = null;
  accountModal.name       = '';
}

async function submitAccountModal() {
  const name = (accountModal.name ?? '').trim();
  if (!name || accountModal.saving) return;
  accountModal.saving = true;

  try {
    if (accountModal.mode === 'edit') {
      if (!can.edit) { closeAccountModal(); return; }
      const target = accountModal.targetType === 'root'
        ? accountModal.root
        : accountModal.node;
      await api.put(`/accounting/accounting-tree/${target.id}`, { name });
      target.name = name;
      closeAccountModal();
      return;
    }

    if (!can.create) { closeAccountModal(); return; }
    const parentId = accountModal.targetType === 'root'
      ? accountModal.root.id
      : accountModal.node.id;
    const parentIdForExpand = parentId;
    await api.post('/accounting/accounting-tree', { name, parent_id: parentIdForExpand, type: 'folder' });
    closeAccountModal();
    await fetchTree();
    expanded[parentIdForExpand]     = true;
    rootExpanded[parentIdForExpand] = true;
  } catch (e) {
    console.error('Save failed', e);
    accountModal.saving = false;
  }
}

/* DELETE */
async function deleteNode() {
  if (!can.edit) return;
  const list = menu.parentList;
  const node = menu.node;
  closeMenu();
  if (!list || !node) return;

  try {
    await api.delete(`/accounting/accounting-tree/${node.id}`);
    const idx = list.findIndex(n => n.id === node.id);
    if (idx !== -1) list.splice(idx, 1);
  } catch (e) {
    console.error('Delete failed', e);
  }
}

function onMenuAction(action) {
  if (action === 'delete') { deleteNode(); return; }
  const snapTargetType = menu.targetType;
  const snapNode       = menu.node;
  const snapRoot       = menu.root;
  const snapParentList = menu.parentList;
  const snapRootObj    = menu.rootObj;
  closeMenu();
  openAccountModal(action, snapTargetType, snapNode, snapRoot, snapParentList, snapRootObj);
}

/* Icons */
const FolderIcon = defineComponent({
  name: 'FolderIcon', inheritAttrs: false,
  setup: (_, { attrs }) => () =>
    h('svg', { ...attrs, width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
      h('path', { d: 'M3 7.5C3 6.11929 4.11929 5 5.5 5H9.2C9.73043 5 10.2391 5.21071 10.6142 5.58579L11.0284 6H18.5C19.8807 6 21 7.11929 21 8.5V16.5C21 17.8807 19.8807 19 18.5 19H5.5C4.11929 19 3 17.8807 3 16.5V7.5Z', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linejoin': 'round' }),
    ]),
});

const FileIcon = defineComponent({
  name: 'FileIcon', inheritAttrs: false,
  setup: (_, { attrs }) => () =>
    h('svg', { ...attrs, width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
      h('path', { d: 'M7 3H14L18 7V21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3Z', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linejoin': 'round' }),
      h('path', { d: 'M14 3V7H18', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linejoin': 'round' }),
    ]),
});

const DotsIcon = defineComponent({
  name: 'DotsIcon', inheritAttrs: false,
  setup: (_, { attrs }) => () =>
    h('svg', { ...attrs, width: 24, height: 24, viewBox: '0 0 24 24', fill: 'currentColor', xmlns: 'http://www.w3.org/2000/svg' }, [
      h('circle', { cx: '12', cy: '5',  r: '1.7' }),
      h('circle', { cx: '12', cy: '12', r: '1.7' }),
      h('circle', { cx: '12', cy: '19', r: '1.7' }),
    ]),
});

const PlusIcon = defineComponent({
  name: 'PlusIcon', inheritAttrs: false,
  setup: (_, { attrs }) => () =>
    h('svg', { ...attrs, width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
      h('path', { d: 'M12 5V19M5 12H19', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round' }),
    ]),
});

const EditIcon = defineComponent({
  name: 'EditIcon', inheritAttrs: false,
  setup: (_, { attrs }) => () =>
    h('svg', { ...attrs, width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
      h('path', { d: 'M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25Z', stroke: 'currentColor', 'stroke-width': '1.8', 'stroke-linejoin': 'round' }),
      h('path', { d: 'M14.06 6.19L17.81 9.94', stroke: 'currentColor', 'stroke-width': '1.8', 'stroke-linejoin': 'round' }),
    ]),
});

const TrashIcon = defineComponent({
  name: 'TrashIcon', inheritAttrs: false,
  setup: (_, { attrs }) => () =>
    h('svg', { ...attrs, width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
      h('path', { d: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
    ]),
});

/* TreeNodeRow */
const TreeNodeRow = defineComponent({
  name: 'TreeNodeRow',
  props: {
    node:     { type: Object, required: true },
    depth:    { type: Number, required: true },
    siblings: { type: Array,  required: true },
    rootObj:  { type: Object, required: true },
  },
  setup(props) {
    const hasChildren = computed(() =>
      Array.isArray(props.node.children) && props.node.children.length > 0
    );
    const isOpen   = computed(() => !!expanded[props.node.id]);
    const isFolder = computed(() => Array.isArray(props.node.children));

    const onCardClick = () => {
      if (isFolder.value) toggleFolder(props.node.id);
    };

    return () =>
      h('div', { class: 'tree-row' }, [
        h('div', {
          class: ['tree-card', isFolder.value && 'tree-card--folder'],
          onClick: onCardClick,
          style: { marginLeft: `${props.depth * 18}px`, display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap' },
        }, [
          h('div', { class: 'tree-left', style: 'display:flex;flex-direction:row;align-items:center;flex-wrap:nowrap;gap:10px;min-width:0;overflow:hidden;margin-top:20px;' }, [
            isFolder.value
              ? h(FolderIcon, { class: 'tree-ico' })
              : h(FileIcon,   { class: 'tree-ico' }),
            h('div', { class: 'tree-code' }, props.node.number),
            h('div', { class: 'tree-text' }, props.node.name),
          ]),
          (can.create || can.edit)
            ? h('button', {
                class: 'tree-dots-btn',
                style: { marginTop: '20px' },
                onClick: (e) => { e.stopPropagation(); openMenu(e, props.node, props.siblings, props.rootObj); },
              }, [h(DotsIcon, { style: { width: '16px', height: '16px' } })])
            : null,
        ]),

        hasChildren.value && isOpen.value
          ? h('div', { class: 'tree-children' },
              props.node.children.map((ch) =>
                h(TreeNodeRow, {
                  node:     ch,
                  depth:    props.depth + 1,
                  siblings: props.node.children,
                  rootObj:  props.rootObj,
                  key:      ch.id,
                })
              )
            )
          : null,
      ]);
  },
});

/* TreeBlock */
const TreeBlock = defineComponent({
  name: 'TreeBlock',
  props: {
    root:      { type: Object, required: true },
    rootIndex: { type: Number, required: true },
  },
  setup(props) {
    const isRootOpen = computed(() => !!rootExpanded[props.root.id]);

    return () =>
      h('div', { class: 'tree-block' }, [
        h('div', {
          class: 'root-head',
          style: 'display:flex;flex-direction:row;align-items:center;justify-content:space-between;flex-wrap:nowrap;cursor:pointer;user-select:none;min-height:48px;background:#eff6ff;border:1.5px solid #bfdbfe;border-radius:14px;padding:8px 14px;transition:background .15s;width:50%;box-sizing:border-box;',
          onClick: () => toggleRoot(props.root.id),
        }, [
          h('div', { class: 'root-head-left',
            style: 'display:flex;flex-direction:row;align-items:center;flex-wrap:nowrap;gap:8px;min-width:0;overflow:hidden;'
          }, [
            h(FolderIcon, { class: 'root-ico' }),
            h('div', { class: 'root-code' }, props.root.number + '.'),
            h('span', { class: 'root-title' }, props.root.name),
          ]),
          (can.create || can.edit)
            ? h('button', {
                class: 'root-dots-btn',
                onClick: (e) => { e.stopPropagation(); openRootMenu(e, props.root); },
                'aria-label': 'Root actions',
              }, [h(DotsIcon, { style: { width: '18px', height: '18px' } })])
            : null,
        ]),

        isRootOpen.value && Array.isArray(props.root.children) && props.root.children.length
          ? h('div', { class: 'tree-root', dir: 'ltr' },
              props.root.children.map((n) =>
                h(TreeNodeRow, {
                  node:     n,
                  depth:    0,
                  siblings: props.root.children,
                  rootObj:  props.root,
                  key:      n.id,
                })
              )
            )
          : null,
      ]);
  },
});
</script>

<style scoped>
svg { width: auto !important; height: auto !important; }
.page-wrap    { padding: 24px; }
.page-center  { display: flex; justify-content: center; width: 100%; }
.page-container { width: min(1000px, 100%); }
.page-title   { font-size: 24px; font-weight: 700; color: #1f2937; margin-bottom: 18px; }
.state-msg        { text-align: center; padding: 40px; font-size: 16px; color: #6b7280; }
.state-msg--err   { color: #dc2626; }
.grid-2 { display: grid; grid-template-columns: 1fr; gap: 36px; }
@media (min-width: 1024px) { .grid-2 { grid-template-columns: 1fr 1fr; } }
.mt-40  { margin-top: 40px; }

.root-head {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 30%;
  flex-wrap: nowrap;
  cursor: pointer;
  user-select: none;
  min-height: 48px;
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 14px;
  padding: 8px 14px;
  transition: background .15s;
}
.root-head:hover { background: #dbeafe; }
.root-head-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  overflow: hidden;
}
.root-ico   { width: 28px; height: 28px; min-width: 28px; color: #2563eb; flex: 0 0 auto; }
.root-code  { font-size: 15px; font-weight: 800; color: #1d4ed8; flex: 0 0 auto; white-space: nowrap; }
.root-title {
  font-size: 16px; font-weight: 700; color: #1e3a8a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  min-width: 0; flex: 1 1 auto;
}
.root-dots-btn {
  width: 36px; height: 36px; min-width: 36px; border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto; background: transparent; border: 0; cursor: pointer;
}
.root-dots-btn:hover { background: rgba(37,99,235,.12); }

.tree-root {
  margin-left: 18px;
  padding-left: 18px;
  border-left: 2px solid #d1d5db;
  margin-top: 8px;
}
.tree-children {
  margin-left: 18px;
  padding-left: 18px;
  border-left: 2px solid #d1d5db;
}
.tree-row + .tree-row { margin-top: 8px; }
.tree-card {
  position: relative;
  border-radius: 14px;
  padding: 10px 12px;
  min-height: 48px;
  width: 75%;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 10px !important;
  flex-wrap: nowrap !important;
  box-sizing: border-box !important;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  transition: background .15s;
}
.tree-card--folder { cursor: pointer; }
.tree-card--folder:hover { background: #f3f4f6; }
.tree-card::before {
  content: '';
  position: absolute;
  left: -18px;
  top: 50%;
  width: 18px;
  border-top: 2px solid #d1d5db;
  transform: translateY(-50%);
}
.tree-left {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  min-width: 0 !important;
  flex: 1 1 auto !important;
}
.tree-ico  { width: 22px; height: 22px; color: #6b7280; opacity: .55; flex: 0 0 auto; }
.tree-code {
  font-size: 13px; font-weight: 800; color: #5b9ac5;
  flex: 0 0 auto; letter-spacing: .01em;
  background: #eff6ff; border-radius: 6px; padding: 2px 6px;
}
.tree-text {
  font-weight: 600; color: #111827; font-size: 14px;
  line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  min-width: 0;
}
.tree-dots-btn {
  width: 36px; height: 36px; min-width: 36px; border-radius: 12px;
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
  flex: 0 0 auto !important; background: transparent; border: 0; cursor: pointer;
}
.tree-dots-btn:hover { background: rgba(0,0,0,.06); }

.menu-overlay { position: fixed; inset: 0; z-index: 999; }
.menu-box {
  position: absolute; width: 220px;
  background: #fff; border-radius: 18px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 18px 40px rgba(0,0,0,.25);
  overflow: hidden;
}
.menu-item {
  width: 100%; padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 0; cursor: pointer;
  font-size: 15px; color: #111827;
}
.menu-item:hover { background: #f7f7f7; }
.menu-item--danger { color: #dc2626; }
.menu-item--danger:hover { background: #fff5f5; }
.menu-ic { width: 20px; height: 20px; }

.acc-overlay {
  position: fixed; inset: 0; z-index: 1200;
  background: rgba(0,0,0,.35);
  display: flex; align-items: center; justify-content: center;
  padding: 18px;
}
.acc-modal {
  width: min(460px, 92vw);
  background: #fff; border-radius: 22px;
  padding: 26px 28px 22px;
  position: relative;
  box-shadow: 0 18px 50px rgba(0,0,0,.35);
}
.acc-x {
  position: absolute; top: 14px; right: 16px;
  width: 38px; height: 38px; border-radius: 10px;
  border: 0; background: transparent;
  font-size: 26px; line-height: 1; cursor: pointer; color: #111827;
}
.acc-title { text-align: center; font-size: 26px; font-weight: 800; margin: 6px 0 22px; color: #111827; }
.acc-label { display: block; font-weight: 700; color: #111827; margin: 0 0 10px; font-size: 16px; }
.acc-input {
  width: 100%; height: 46px; border-radius: 10px;
  border: 1px solid #d1d5db; padding: 0 14px; font-size: 16px; outline: none;
  box-sizing: border-box;
}
.acc-input:focus { border-color: #5b9ac5; box-shadow: 0 0 0 3px rgba(91,154,197,.18); }
.acc-btn {
  width: 100%; margin-top: 18px; height: 46px;
  border: 0; border-radius: 999px;
  background: #5b9ac5; color: #fff;
  font-weight: 800; font-size: 16px; cursor: pointer;
}
.acc-btn:disabled { opacity: .6; cursor: not-allowed; }
.acc-btn:not(:disabled):hover { filter: brightness(.95); }
</style>
